import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RestoreIcon from '@mui/icons-material/Restore';

import Client from './components/Client';
import Config from './components/ClientConfig';
import PromotionClaimRecoveryPanel from './components/PromotionClaimRecoveryPanel';
import CustomPeriodPopover, { validateCustomPeriod } from './components/biReports/CustomPeriodPopover';


export const CLAIM_STATUSES = [
  {
    value: 0,
    label: 'Registered',
    color: 'default',
    bar: '#94a3b8',
    bg: '#f1f5f9',
    text: '#334155',
  },
  {
    value: 1,
    label: 'Claimed',
    color: 'info',
    bar: '#3b82f6',
    bg: '#eff6ff',
    text: '#1e3a8a',
  },
  {
    value: 2,
    label: 'Consumed',
    color: 'success',
    bar: '#22c55e',
    bg: '#f0fdf4',
    text: '#166534',
  },
  {
    value: 3,
    label: 'Missed',
    color: 'error',
    bar: '#ef4444',
    bg: '#fef2f2',
    text: '#991b1b',
  },
  {
    value: 4,
    label: 'Pending',
    color: 'warning',
    bar: '#f59e0b',
    bg: '#fffbeb',
    text: '#92400e',
  },
];

const CLIENT_USER_STATUSES = {
  0: 'Registered',
  1: 'Active',
  2: 'Suspended',
  8: 'Pending',
  9: 'Unverified',
};

const PERIOD_LABELS = {
  custom: 'Custom period',
  all: 'All time',
  today: 'Today',
  last_7d: 'Last 7 days',
  last_30d: 'Last 30 days',
  this_month: 'This month',
  last_90d: 'Last 90 days',
};

const VIEW_ROLES = [32, 34, 40, 50];
const RECOVERABLE_CLAIM_STATUSES = [2, 3, 4];
const CLAIM_TIME_ZONE = 'Europe/Bucharest';

export function canViewClientPromotionClaims(userType) {
  return VIEW_ROLES.includes(Number(userType));
}

export function canRecoverPromotionClaims(userType) {
  return Number(userType) === 34;
}

function currentUserType() {
  try {
    return JSON.parse(localStorage.getItem('apiUser'))?.profile?.user_type;
  } catch (_error) {
    return null;
  }
}

function formatDateInput(date) {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function getClaimPeriodRange(periodMode, anchorDate = new Date()) {
  if (periodMode === 'all') return {};

  const businessParts = new Intl.DateTimeFormat('en-GB', {
    timeZone: CLAIM_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(anchorDate).reduce((result, part) => {
    if (part.type !== 'literal') result[part.type] = Number(part.value);
    return result;
  }, {});
  const dateTo = new Date(Date.UTC(
    businessParts.year,
    businessParts.month - 1,
    businessParts.day,
    12,
  ));
  const dateFrom = new Date(dateTo);

  if (periodMode === 'last_7d') {
    dateFrom.setUTCDate(dateTo.getUTCDate() - 6);
  } else if (periodMode === 'last_30d') {
    dateFrom.setUTCDate(dateTo.getUTCDate() - 29);
  } else if (periodMode === 'this_month') {
    dateFrom.setUTCDate(1);
  } else if (periodMode === 'last_90d') {
    dateFrom.setUTCDate(dateTo.getUTCDate() - 89);
  }

  return {
    dateFrom: formatDateInput(dateFrom),
    dateTo: formatDateInput(dateTo),
  };
}

function formatClaimNumber(value, fractionDigits = 0, showPositiveSign = false) {
  if (value === null || value === undefined || String(value).trim() === '') return '—';
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return '—';

  const formattedValue = numberValue.toLocaleString('ro-RO', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
  return `${showPositiveSign && numberValue > 0 ? '+' : ''}${formattedValue}`;
}

export function formatClaimDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: CLAIM_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date).reduce((result, part) => {
    if (part.type !== 'literal') result[part.type] = part.value;
    return result;
  }, {});

  return `${parts.day}-${parts.month}-${parts.year} ${parts.hour}:${parts.minute}`;
}

function responseErrorMessage(response, body) {
  if (response?.status === 401) {
    return 'Your session expired. Sign in again before searching.';
  }
  if (response?.status === 403) {
    return 'Your account is not authorized to inspect client promotion claims.';
  }

  switch (body?.code) {
    case 'client_not_found':
      return 'No client account was found for this email.';
    case 'claim_not_found':
      return 'No promotion claim was found with this ID.';
    case 'ambiguous_client_email':
      return 'More than one client uses this email. The data was not shown.';
    case 'invalid_filters': {
      const errors = body.errors || {};
      const firstError = Object.values(errors).flat()[0];
      return firstError || 'Check the email and filters.';
    }
    default:
      return body?.detail || body?.message || 'Promotion claims could not be loaded.';
  }
}

function statusDefinition(value) {
  return CLAIM_STATUSES.find((claimStatus) => (
    claimStatus.value === Number(value)
  )) || CLAIM_STATUSES[0];
}

function promotionTarget(promotion) {
  if (promotion?.product) return `Product #${promotion.product}`;
  if (promotion?.combo) return `Combo #${promotion.combo}`;
  return 'Voucher / general';
}

function CompactMetric({ title, value, subtitle, tone = 'default' }) {
  const colors = {
    default: '#020617',
    positive: '#166534',
    warning: '#92400e',
    neutral: '#1e3a8a',
  };

  return (
    <Box
      sx={{
        minWidth: 0,
        px: { xs: 1, sm: 1.25 },
        py: { xs: 0.75, sm: 0.9 },
        borderRadius: '14px',
        bgcolor: '#f8fafc',
        border: '1px solid rgba(15, 23, 42, 0.06)',
        height: '100%',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <Typography sx={{ color: '#64748b', fontSize: '0.7rem', fontWeight: 800, lineHeight: 1.15 }}>
        {title}
      </Typography>
      <Typography sx={{ color: colors[tone], fontSize: '1.08rem', fontWeight: 950, lineHeight: 1.15, mt: 0.25 }}>
        {value}
      </Typography>
      <Typography sx={{ color: '#94a3b8', fontSize: '0.64rem', lineHeight: 1.15, mt: 0.25 }}>
        {subtitle}
      </Typography>
    </Box>
  );
}

function PanelHeader({ title, subtitle, action = null }) {
  return (
    <Box
      sx={{
        p: { xs: 1.25, sm: 1.5, md: 1.75 },
        pb: 1.1,
        flexShrink: 0,
        borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 950, color: '#020617', lineHeight: 1.2 }}>
            {title}
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.76rem', lineHeight: 1.35, mt: 0.25 }}>
            {subtitle}
          </Typography>
        </Box>
        {action ? <Box sx={{ flexShrink: 0 }}>{action}</Box> : null}
      </Stack>
    </Box>
  );
}

function StatusDistribution({ hasData, summary }) {
  const maxCount = Math.max(
    1,
    ...CLAIM_STATUSES.map((claimStatus) => (
      Number(summary.byStatus?.[String(claimStatus.value)] || 0)
    )),
  );

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: { xs: '14px', sm: '16px' },
        border: '1px solid rgba(15, 23, 42, 0.06)',
        boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)',
        bgcolor: '#ffffff',
        height: { xs: '100%', md: 'auto' },
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <PanelHeader
        title="Claim status summary"
        subtitle="Issued claims by current lifecycle state"
      />
      <Box sx={{ p: { xs: 1.25, sm: 1.5 }, flex: { xs: 1, md: 'initial' }, minHeight: 0, overflowY: 'auto' }}>
        {!hasData ? (
          <Box
            sx={{
              height: '100%',
              minHeight: 120,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px dashed rgba(100, 116, 139, 0.3)',
              borderRadius: '14px',
              bgcolor: '#f8fafc',
              px: 2,
            }}
          >
            <Typography variant="body2" sx={{ color: '#64748b', textAlign: 'center' }}>
              Loading the issued-claim summary…
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1}>
            {CLAIM_STATUSES.map((claimStatus) => {
              const count = Number(summary.byStatus?.[String(claimStatus.value)] || 0);
              const width = count > 0 ? Math.max(3, (count / maxCount) * 100) : 0;
              return (
                <Box key={claimStatus.value}>
                  <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 0.35 }}>
                    <Typography sx={{ color: claimStatus.text, fontSize: '0.74rem', fontWeight: 850 }}>
                      {claimStatus.label}
                    </Typography>
                    <Typography sx={{ color: '#334155', fontSize: '0.74rem', fontWeight: 900 }}>
                      {formatClaimNumber(count)}
                    </Typography>
                  </Stack>
                  <Box sx={{ height: 9, bgcolor: claimStatus.bg, borderRadius: 99, overflow: 'hidden' }}>
                    <Box
                      sx={{
                        width: `${width}%`,
                        height: '100%',
                        bgcolor: claimStatus.bar,
                        borderRadius: 99,
                      }}
                    />
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
    </Paper>
  );
}

function ClaimStatusChip({ claim }) {
  const definition = statusDefinition(claim.status);
  return (
    <Chip
      size="small"
      label={claim.statusLabel || definition.label}
      sx={{ bgcolor: definition.bg, color: definition.text, fontWeight: 850 }}
    />
  );
}

function ClaimActivity({
  hasData,
  claims,
  filteredTotal,
  nextCursor,
  loadingMore,
  filtersDirty,
  onLoadMore,
  canRecover,
  recoverySubmitting,
  onRecover,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: { xs: '14px', sm: '16px' },
        border: '1px solid rgba(15, 23, 42, 0.06)',
        boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)',
        bgcolor: '#ffffff',
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <PanelHeader
        title="Claim activity"
        subtitle={hasData
          ? `Showing ${formatClaimNumber(claims.length)} of ${formatClaimNumber(filteredTotal)} matching claims`
          : 'Newest claims and linked orders'}
      />

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', p: { xs: 1, md: 0 } }}>
        {!hasData || claims.length === 0 ? (
          <Box
            sx={{
              height: '100%',
              minHeight: 150,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: { xs: '#f8fafc', md: '#ffffff' },
              borderRadius: { xs: '14px', md: 0 },
              px: 2,
            }}
          >
            <Typography variant="body2" sx={{ color: '#64748b', textAlign: 'center' }}>
              {hasData
                ? 'No promotion claims match the selected filters.'
                : 'Loading promotion claim activity…'}
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ display: { xs: 'none', md: 'block' }, height: '100%', overflow: 'auto' }}>
              <Table stickyHeader size="small" aria-label="Client promotion claims">
                <TableHead>
                  <TableRow>
                    <TableCell>Claim</TableCell>
                    <TableCell>Promotion</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Claimed</TableCell>
                    <TableCell>Consumed</TableCell>
                    <TableCell>Orders</TableCell>
                    {canRecover ? <TableCell align="right">Action</TableCell> : null}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {claims.map((claim) => {
                    const promotion = claim.promotion || {};
                    return (
                      <TableRow key={claim.iref} hover>
                        <TableCell sx={{ fontWeight: 900 }}>#{claim.iref}</TableCell>
                        <TableCell sx={{ minWidth: 180 }}>
                          <Typography sx={{ fontSize: '0.8rem', fontWeight: 850 }}>
                            {promotion.name || 'Unnamed promotion'}
                          </Typography>
                          <Typography sx={{ color: '#64748b', fontSize: '0.68rem' }}>
                            #{promotion.iref || '—'} · {promotionTarget(promotion)} · value {formatClaimNumber(promotion.value, 2)}
                          </Typography>
                        </TableCell>
                        <TableCell><ClaimStatusChip claim={claim} /></TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatClaimDateTime(claim.claimedOn)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatClaimDateTime(claim.consumedOn)}</TableCell>
                        <TableCell sx={{ minWidth: 150 }}>
                          {claim.linkedOrders?.length
                            ? claim.linkedOrders.map((orderLink) => `#${orderLink.iref}`).join(', ')
                            : '—'}
                        </TableCell>
                        {canRecover ? (
                          <TableCell align="right">
                            {RECOVERABLE_CLAIM_STATUSES.includes(Number(claim.status)) ? (
                              <Button
                                type="button"
                                size="small"
                                variant="outlined"
                                color="warning"
                                startIcon={<RestoreIcon />}
                                onClick={() => onRecover(claim)}
                                disabled={recoverySubmitting}
                                aria-label={`Recover claim #${claim.iref}`}
                                sx={{ textTransform: 'none', fontWeight: 850 }}
                              >
                                Recover
                              </Button>
                            ) : '—'}
                          </TableCell>
                        ) : null}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Stack
              spacing={0.8}
              sx={{ display: { xs: 'flex', md: 'none' }, height: '100%', overflowY: 'auto' }}
            >
              {claims.map((claim) => {
                const promotion = claim.promotion || {};
                return (
                  <Paper
                    key={claim.iref}
                    elevation={0}
                    sx={{ p: 1.1, borderRadius: '14px', border: '1px solid rgba(15, 23, 42, 0.06)', bgcolor: '#f8fafc' }}
                  >
                    <Stack direction="row" justifyContent="space-between" spacing={1}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.84rem', fontWeight: 950 }}>
                          #{claim.iref} · {promotion.name || 'Unnamed promotion'}
                        </Typography>
                        <Typography sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                          {formatClaimDateTime(claim.claimedOn)}
                        </Typography>
                      </Box>
                      <ClaimStatusChip claim={claim} />
                    </Stack>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.75, gap: 0.5 }}>
                      <Chip size="small" variant="outlined" label={promotionTarget(promotion)} />
                      {claim.linkedOrders?.map((orderLink) => (
                        <Chip key={orderLink.iref} size="small" variant="outlined" label={`Order #${orderLink.iref}`} />
                      ))}
                    </Stack>
                    {canRecover && RECOVERABLE_CLAIM_STATUSES.includes(Number(claim.status)) ? (
                      <Button
                        type="button"
                        size="small"
                        variant="outlined"
                        color="warning"
                        startIcon={<RestoreIcon />}
                        onClick={() => onRecover(claim)}
                        disabled={recoverySubmitting}
                        aria-label={`Recover claim #${claim.iref}`}
                        sx={{ mt: 0.9, textTransform: 'none', fontWeight: 850 }}
                      >
                        Recover
                      </Button>
                    ) : null}
                  </Paper>
                );
              })}
            </Stack>
          </>
        )}
      </Box>

      {nextCursor ? (
        <Box sx={{ p: 1, flexShrink: 0, borderTop: '1px solid rgba(15, 23, 42, 0.06)', textAlign: 'center' }}>
          <Button
            type="button"
            size="small"
            variant="outlined"
            disabled={loadingMore || filtersDirty || recoverySubmitting}
            onClick={onLoadMore}
            sx={{ textTransform: 'none', fontWeight: 850 }}
          >
            {loadingMore
              ? <CircularProgress size={17} />
              : `Load more (${formatClaimNumber(claims.length)}/${formatClaimNumber(filteredTotal)})`}
          </Button>
        </Box>
      ) : null}
    </Paper>
  );
}

function DailyPromotionActivity({ hasData, rows, periodMode }) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: { xs: '14px', sm: '16px' },
        border: '1px solid rgba(15, 23, 42, 0.06)',
        boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)',
        bgcolor: '#ffffff',
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <PanelHeader
        title="Daily promotion activity"
        subtitle={`All clients · ${PERIOD_LABELS[periodMode]}`}
      />

      <Box sx={{ p: { xs: 1, sm: 1.25, md: 1.5 }, flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <TableContainer
          sx={{
            height: '100%',
            overflow: 'auto',
            border: '1px solid rgba(15, 23, 42, 0.06)',
            borderRadius: '12px',
          }}
        >
          <Table stickyHeader size="small" aria-label="Daily promotion claim activity">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 950, bgcolor: '#f8fafc' }}>Date</TableCell>
                <TableCell align="right" sx={{ fontWeight: 950, bgcolor: '#f8fafc' }}>Issued</TableCell>
                <TableCell align="right" sx={{ fontWeight: 950, bgcolor: '#f8fafc' }}>Used</TableCell>
                <TableCell align="right" sx={{ fontWeight: 950, bgcolor: '#f8fafc' }}>Net</TableCell>
                <TableCell align="right" sx={{ fontWeight: 950, bgcolor: '#f8fafc' }}>Activity</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!hasData || rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" sx={{ py: 3, textAlign: 'center', color: '#64748b' }}>
                      {hasData
                        ? 'No promotion activity was found for the selected period.'
                        : 'Loading daily promotion activity…'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}

              {rows.map((row) => (
                <TableRow key={row.date} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap', color: '#334155', fontWeight: 800 }}>
                    {row.date}
                  </TableCell>
                  <TableCell align="right" sx={{ color: '#1e3a8a', fontWeight: 950 }}>
                    {formatClaimNumber(row.issued)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: '#166534', fontWeight: 950 }}>
                    {formatClaimNumber(row.used)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ color: Number(row.net) >= 0 ? '#1e3a8a' : '#991b1b', fontWeight: 950 }}
                  >
                    {formatClaimNumber(row.net, 0, true)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: '#334155', fontWeight: 850 }}>
                    {formatClaimNumber(row.activity)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Paper>
  );
}

function ClientLookupPanel({
  lookupMode,
  setLookupMode,
  email,
  setEmail,
  claimIref,
  setClaimIref,
  selectedStatus,
  setSelectedStatus,
  periodMode,
  scope,
  hasData,
  client,
  loading,
  loadingMore,
  filtersDirty,
  onSearch,
  onClear,
  canRecover,
  recoveryClaim,
  recoveryResult,
  recoverySubmitting,
  matchedClaim,
  onRecoveryStart,
  onRecoveryCancel,
  onRecovered,
  onRecoverySubmittingChange,
}) {
  let lookupButtonLabel = 'Refresh all clients';
  if (loading) {
    lookupButtonLabel = 'Loading…';
  } else if (lookupMode === 'claim') {
    if (!claimIref) {
      lookupButtonLabel = 'Refresh all clients';
    } else {
      lookupButtonLabel = matchedClaim ? 'Refresh claim' : 'Find claim';
    }
  } else if (email.trim()) {
    lookupButtonLabel = client ? 'Refresh client' : 'Search client';
  }

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: { xs: '14px', sm: '16px' },
        border: '1px solid rgba(15, 23, 42, 0.06)',
        boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)',
        bgcolor: '#ffffff',
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <PanelHeader
        title="Client lookup"
        subtitle="Search and filter claim history"
        action={client ? (
          <Button type="button" size="small" onClick={onClear} disabled={recoverySubmitting} sx={{ textTransform: 'none', fontWeight: 800 }}>
            Clear
          </Button>
        ) : null}
      />

      <Box sx={{ p: { xs: 1.25, sm: 1.5, md: 1.75 }, flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <Box component="form" onSubmit={onSearch}>
          <Stack spacing={1.25}>
            <Stack direction="row" spacing={0.75} alignItems="flex-start">
              {lookupMode === 'claim' ? (
                <TextField
                  label="Promo claim ID"
                  size="small"
                  value={claimIref}
                  onChange={(event) => setClaimIref(event.target.value)}
                  fullWidth
                  autoFocus
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    maxLength: 10,
                  }}
                  helperText="Leave empty to show all clients."
                  disabled={recoverySubmitting}
                  sx={{ minWidth: 0 }}
                />
              ) : (
                <TextField
                  label="Client email"
                  type="email"
                  size="small"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  fullWidth
                  autoFocus
                  inputProps={{ maxLength: 254 }}
                  helperText="Leave empty to show all clients."
                  disabled={recoverySubmitting}
                  sx={{ minWidth: 0 }}
                />
              )}
              <ToggleButtonGroup
                exclusive
                size="small"
                value={lookupMode}
                onChange={(_event, nextMode) => {
                  if (nextMode) setLookupMode(nextMode);
                }}
                disabled={recoverySubmitting}
                aria-label="Client lookup method"
                sx={{
                  flexShrink: 0,
                  bgcolor: '#fff',
                  '& .MuiToggleButton-root': {
                    minWidth: 0,
                    height: 40,
                    px: 0.85,
                    color: '#64748b',
                    borderColor: 'rgba(100, 116, 139, 0.28)',
                    fontSize: '0.68rem',
                    fontWeight: 850,
                    textTransform: 'none',
                    '&:hover': { bgcolor: '#f8fafc' },
                    '&.Mui-selected': {
                      bgcolor: '#e2e8f0',
                      color: '#1e3a8a',
                      '&:hover': { bgcolor: '#cbd5e1' },
                    },
                  },
                }}
              >
                <ToggleButton value="email">Email</ToggleButton>
                <ToggleButton
                  value="claim"
                  aria-label="Claim ID"
                  title="Use the ID from the error log. The exact claim stays visible outside the selected period."
                >
                  ID
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            <TextField
              select
              label="Claim status"
              size="small"
              value={selectedStatus}
              onChange={(event) => setSelectedStatus(event.target.value)}
              fullWidth
              disabled={recoverySubmitting}
            >
              <MenuItem value="all">All statuses</MenuItem>
              {CLAIM_STATUSES.map((claimStatus) => (
                <MenuItem key={claimStatus.value} value={String(claimStatus.value)}>
                  {claimStatus.label}
                </MenuItem>
              ))}
            </TextField>
            <Button
              type="submit"
              variant="contained"
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
              disabled={
                loading
                || loadingMore
                || recoverySubmitting
              }
              sx={{ bgcolor: '#0f172a', textTransform: 'none', fontWeight: 900, borderRadius: '10px', '&:hover': { bgcolor: '#111827' } }}
            >
              {lookupButtonLabel}
            </Button>
          </Stack>
        </Box>

        <Box sx={{ mt: 1.5 }}>
          {!client ? (
            <Box sx={{ p: 1.35, borderRadius: '14px', bgcolor: '#f8fafc', border: '1px dashed rgba(100, 116, 139, 0.3)' }}>
              <Typography sx={{ color: '#334155', fontWeight: 850, mb: 0.35 }}>
                {hasData && scope === 'allClients'
                  ? 'All clients'
                  : loading ? 'Loading claims' : 'Claims unavailable'}
              </Typography>
              <Typography sx={{ color: '#64748b', fontSize: '0.76rem' }}>
                {hasData && scope === 'allClients'
                  ? `Showing authorized claim data for ${PERIOD_LABELS[periodMode].toLowerCase()}.`
                  : loading
                    ? 'Preparing the all-client claim view.'
                    : 'Use refresh to try loading the all-client view again.'}
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1.25}>
              <Box sx={{ p: 1.25, borderRadius: '14px', bgcolor: '#f8fafc', border: '1px solid rgba(15, 23, 42, 0.06)' }}>
                <Stack direction="row" justifyContent="space-between" spacing={1}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ color: '#020617', fontWeight: 950, lineHeight: 1.15 }}>
                      {client.name || 'Client'}
                    </Typography>
                    <Typography sx={{ color: '#64748b', fontSize: '0.74rem', wordBreak: 'break-word' }}>
                      {client.email}
                    </Typography>
                    <Typography sx={{ color: '#94a3b8', fontSize: '0.68rem', mt: 0.35 }}>
                      Client #{client.iref}
                    </Typography>
                  </Box>
                  <Chip size="small" label="Selected" sx={{ bgcolor: '#eff6ff', color: '#1e3a8a', fontWeight: 900 }} />
                </Stack>
                <Stack direction="row" spacing={0.6} flexWrap="wrap" sx={{ mt: 1, gap: 0.6 }}>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={CLIENT_USER_STATUSES[client.userStatus] || `Account status ${client.userStatus}`}
                  />
                  <Chip size="small" variant="outlined" label={PERIOD_LABELS[periodMode]} />
                  {client.erased ? <Chip size="small" color="warning" label="Erased client" /> : null}
                </Stack>
              </Box>

              {matchedClaim ? (
                <Box sx={{ p: 1.25, borderRadius: '14px', bgcolor: '#eff6ff', border: '1px solid #bfdbfe' }}>
                  <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ color: '#1e3a8a', fontWeight: 950 }}>
                        Matched claim #{matchedClaim.iref}
                      </Typography>
                      <Typography sx={{ color: '#475569', fontSize: '0.74rem', mt: 0.2 }}>
                        {matchedClaim.promotion?.name || 'Unnamed promotion'}
                      </Typography>
                    </Box>
                    <ClaimStatusChip claim={matchedClaim} />
                  </Stack>
                  <Typography sx={{ color: '#64748b', fontSize: '0.7rem', mt: 0.75 }}>
                    Claimed {formatClaimDateTime(matchedClaim.claimedOn)} · Consumed {formatClaimDateTime(matchedClaim.consumedOn)}
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.75, gap: 0.5 }}>
                    <Chip size="small" variant="outlined" label={promotionTarget(matchedClaim.promotion || {})} />
                    {matchedClaim.linkedOrders?.map((orderLink) => (
                      <Chip key={orderLink.iref} size="small" variant="outlined" label={`Order #${orderLink.iref}`} />
                    ))}
                  </Stack>
                  {canRecover && RECOVERABLE_CLAIM_STATUSES.includes(Number(matchedClaim.status)) ? (
                    <Button
                      type="button"
                      size="small"
                      variant="outlined"
                      color="warning"
                      startIcon={<RestoreIcon />}
                      onClick={() => onRecoveryStart(matchedClaim)}
                      disabled={recoverySubmitting}
                      aria-label={`Recover matched claim #${matchedClaim.iref}`}
                      sx={{ mt: 0.9, textTransform: 'none', fontWeight: 850 }}
                    >
                      Recover matched claim
                    </Button>
                  ) : null}
                </Box>
              ) : null}

              {filtersDirty ? (
                <Alert severity="info" sx={{ borderRadius: '12px' }}>
                  Filters changed. Refresh claims to apply them.
                </Alert>
              ) : null}

              {canRecover ? (
                <>
                  {recoveryResult?.code === 'stale_claim_state' ? (
                    <Alert severity="warning" sx={{ borderRadius: '12px' }}>
                      The claim changed while you were reviewing it. Its latest data was loaded; review it before trying again.
                    </Alert>
                  ) : recoveryResult ? (
                    <Alert severity="success" sx={{ borderRadius: '12px' }}>
                      <Typography sx={{ fontWeight: 900 }}>
                        {recoveryResult.changed
                          ? 'Promotion claim recovered'
                          : 'Promotion claim was already available'}
                      </Typography>
                      <Typography variant="body2">
                        Claim #{recoveryResult.claim?.iref} is now Claimed.
                        {recoveryResult.auditLogId
                          ? ` Audit log #${recoveryResult.auditLogId}.`
                          : ''}
                      </Typography>
                    </Alert>
                  ) : null}
                  <PromotionClaimRecoveryPanel
                    claim={recoveryClaim}
                    onCancel={onRecoveryCancel}
                    onRecovered={onRecovered}
                    onSubmittingChange={onRecoverySubmittingChange}
                  />
                </>
              ) : (
                <Alert severity="info" sx={{ borderRadius: '12px' }}>
                  Claim recovery is restricted to HQ Platform users and remains fully audited.
                </Alert>
              )}
            </Stack>
          )}
        </Box>
      </Box>
    </Paper>
  );
}

function ClientPromotionClaims({ onReady }) {
  const clientApiRef = useRef(null);
  const abortControllerRef = useRef(null);
  const requestIdRef = useRef(0);
  if (!clientApiRef.current) clientApiRef.current = new Client('none');
  const userType = currentUserType();
  const authorized = canViewClientPromotionClaims(userType);
  const canRecover = canRecoverPromotionClaims(userType);

  const [email, setEmailState] = useState('');
  const [claimIref, setClaimIrefState] = useState('');
  const [lookupMode, setLookupModeState] = useState('email');
  const [selectedStatus, setSelectedStatusState] = useState('all');
  const [periodMode, setPeriodMode] = useState('this_month');
  const customPeriodAnchor = useRef(null);
  const [customRange, setCustomRange] = useState(null);
  const [customPeriodOpen, setCustomPeriodOpen] = useState(false);
  const [draftRange, setDraftRange] = useState({ dateFrom: '', dateTo: '' });
  const [customPeriodError, setCustomPeriodError] = useState('');
  const [scope, setScope] = useState('allClients');
  const [hasData, setHasData] = useState(false);
  const [client, setClient] = useState(null);
  const [claims, setClaims] = useState([]);
  const [dailyActivity, setDailyActivity] = useState([]);
  const [summary, setSummary] = useState({ total: 0, filteredTotal: 0, byStatus: {} });
  const [nextCursor, setNextCursor] = useState(null);
  const [activeQuery, setActiveQuery] = useState(null);
  const [filtersDirty, setFiltersDirty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [recoveryClaim, setRecoveryClaim] = useState(null);
  const [recoveryResult, setRecoveryResult] = useState(null);
  const [recoverySubmitting, setRecoverySubmitting] = useState(false);
  const [matchedClaim, setMatchedClaim] = useState(null);

  const setEmail = (value) => {
    setEmailState(value);
    if (String(value).trim()) setClaimIrefState('');
    if (hasData) setFiltersDirty(true);
    setError('');
    setMatchedClaim(null);
    setRecoveryClaim(null);
    setRecoveryResult(null);
  };

  const setClaimIref = (value) => {
    const normalizedValue = String(value || '').replace(/\D/g, '').slice(0, 10);
    setClaimIrefState(normalizedValue);
    if (normalizedValue) setEmailState('');
    if (hasData) setFiltersDirty(true);
    setError('');
    setMatchedClaim(null);
    setRecoveryClaim(null);
    setRecoveryResult(null);
  };

  const setLookupMode = (nextMode) => {
    if (nextMode === lookupMode || recoverySubmitting) return;
    setLookupModeState(nextMode);
    setEmailState('');
    setClaimIrefState('');
    if (hasData) setFiltersDirty(true);
    setError('');
    setMatchedClaim(null);
    setRecoveryClaim(null);
    setRecoveryResult(null);
  };

  const setSelectedStatus = (value) => {
    setSelectedStatusState(value);
    if (hasData) setFiltersDirty(true);
    setError('');
    setRecoveryClaim(null);
    setRecoveryResult(null);
  };

  const buildQuery = ({
    emailValue = email,
    claimIrefValue = claimIref,
    statusValue = selectedStatus,
    periodValue = periodMode,
    customRangeValue = customRange,
  } = {}) => {
    const query = {
      ...(periodValue === 'custom' ? customRangeValue : getClaimPeriodRange(periodValue)),
    };
    const normalizedEmail = String(emailValue || '').trim().toLowerCase();
    const normalizedClaimIref = String(claimIrefValue || '').trim();
    if (normalizedClaimIref) {
      query.claimIref = Number(normalizedClaimIref);
    } else if (normalizedEmail) {
      query.email = normalizedEmail;
    }
    if (statusValue !== 'all') query.status = Number(statusValue);
    return query;
  };

  const loadPage = async (query, { append = false, cursor = null } = {}) => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    abortControllerRef.current = controller;
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setHasData(false);
      setClient(null);
      setClaims([]);
      setDailyActivity([]);
      setMatchedClaim(null);
      setSummary({ total: 0, filteredTotal: 0, byStatus: {} });
      setNextCursor(null);
    }
    setError('');

    const cursorQuery = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
    const requestOptions = {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${clientApiRef.current.getAuthToken()}`,
      },
      body: JSON.stringify(query),
    };
    if (controller) requestOptions.signal = controller.signal;

    try {
      const response = await fetch(
        `${clientApiRef.current.prefix}${Config.API_URL_PROMOTION_CLAIMS}for-client/${cursorQuery}`,
        requestOptions,
      );
      let body = {};
      try {
        body = await response.json();
      } catch (_error) {
        body = {};
      }

      if (requestId !== requestIdRef.current) return;
      if (!response.ok || body.status !== 'SUCCESS') {
        setError(responseErrorMessage(response, body));
        return;
      }

      setClient(body.client || null);
      setScope(body.scope || (body.client ? 'client' : 'allClients'));
      setHasData(true);
      setSummary(body.summary || { total: 0, filteredTotal: 0, byStatus: {} });
      setClaims((current) => (
        append ? [...current, ...(body.claims || [])] : (body.claims || [])
      ));
      if (!append) setDailyActivity(body.dailyActivity || []);
      if (!append) setMatchedClaim(body.matchedClaim || null);
      setNextCursor(body.pagination?.nextCursor || null);
      setActiveQuery(query);
      setFiltersDirty(false);
    } catch (requestError) {
      if (requestError?.name !== 'AbortError' && requestId === requestIdRef.current) {
        setError('The backend could not be reached. No claim data was loaded.');
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setLoadingMore(false);
        abortControllerRef.current = null;
      }
    }
  };

  useEffect(() => {
    if (onReady) onReady();
    if (authorized) {
      loadPage(buildQuery({
        emailValue: '',
        claimIrefValue: '',
        statusValue: 'all',
        periodValue: 'this_month',
      }));
    }
    return () => {
      requestIdRef.current += 1;
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
    // Initial authorized all-client load only; later refreshes are explicit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorized, onReady]);

  const handleSearch = (event) => {
    event.preventDefault();
    if (loading || loadingMore || recoverySubmitting) return;
    setRecoveryClaim(null);
    setRecoveryResult(null);
    setMatchedClaim(null);
    loadPage(buildQuery());
  };

  const applyPeriod = (nextPeriodMode, nextCustomRange = customRange) => {
    if (loading || loadingMore || recoverySubmitting) return;
    setPeriodMode(nextPeriodMode);
    setError('');
    setRecoveryClaim(null);
    setRecoveryResult(null);
    const scopeClaimIref = scope === 'client'
      ? (activeQuery?.claimIref || '')
      : '';
    const scopeEmail = scope === 'client' && !scopeClaimIref
      ? (activeQuery?.email || client?.email || '')
      : '';
    setEmailState(scopeEmail);
    setClaimIrefState(String(scopeClaimIref));
    setLookupModeState(
      scopeClaimIref || (scope === 'allClients' && lookupMode === 'claim')
        ? 'claim'
        : 'email',
    );
    loadPage(buildQuery({
      emailValue: scopeEmail,
      claimIrefValue: scopeClaimIref,
      statusValue: selectedStatus,
      periodValue: nextPeriodMode,
      customRangeValue: nextCustomRange,
    }));
  };

  const openCustomPeriod = () => {
    setDraftRange(periodMode === 'custom' && customRange
      ? { ...customRange }
      : getClaimPeriodRange(periodMode === 'all' ? 'last_30d' : periodMode));
    setCustomPeriodError('');
    setCustomPeriodOpen(true);
  };

  const handlePeriodChange = (_event, nextPeriodMode) => {
    if (!nextPeriodMode || loading || loadingMore || recoverySubmitting) return;
    if (nextPeriodMode === 'custom') openCustomPeriod();
    else applyPeriod(nextPeriodMode);
  };

  const handleCustomPeriodApply = () => {
    if (loading || loadingMore || recoverySubmitting) return;
    const validationError = validateCustomPeriod(draftRange.dateFrom, draftRange.dateTo);
    setCustomPeriodError(validationError);
    if (validationError) return;
    setCustomPeriodOpen(false);
    if (periodMode === 'custom' && customRange?.dateFrom === draftRange.dateFrom
      && customRange?.dateTo === draftRange.dateTo) return;
    setCustomRange({ ...draftRange });
    applyPeriod('custom', draftRange);
  };

  const handleClear = () => {
    if (recoverySubmitting) return;
    setEmailState('');
    setClaimIrefState('');
    setLookupModeState('email');
    setSelectedStatusState('all');
    setFiltersDirty(false);
    setError('');
    setRecoveryClaim(null);
    setRecoveryResult(null);
    setMatchedClaim(null);
    loadPage(buildQuery({
      emailValue: '',
      claimIrefValue: '',
      statusValue: 'all',
      periodValue: periodMode,
    }));
  };

  const handleRecovered = (result) => {
    setRecoveryClaim(null);
    setRecoveryResult(result || { code: 'stale_claim_state' });
    loadPage(activeQuery || buildQuery());
  };

  const handleRecoveryStart = (claim) => {
    setRecoveryClaim(claim);
    setRecoveryResult(null);
  };

  if (!authorized) {
    return (
      <Box sx={{ p: 2, bgcolor: 'background.default', minHeight: '100%' }}>
        <Alert severity="error">
          This page is available only to authorized Magic administrators.
        </Alert>
      </Box>
    );
  }

  const statusCounts = summary.byStatus || {};
  const needsReview = Number(statusCounts['3'] || 0) + Number(statusCounts['4'] || 0);
  const filteredTotal = Number(summary.filteredTotal ?? summary.total ?? 0);
  const pageSx = {
    height: { xs: 'auto', md: 'calc(100vh - 88px)' },
    minHeight: { xs: '100vh', md: 640 },
    width: {
      xs: 'calc(100vw - 8px)',
      sm: '100%',
    },
    maxWidth: {
      xs: 'calc(100vw - 8px)',
      sm: '100%',
    },
    ml: {
      xs: 'calc(50% - 50vw + 4px)',
      sm: 0,
    },
    overflow: { xs: 'visible', md: 'hidden' },
    pt: { xs: 0.75, sm: 1.5, md: 2 },
    pb: { xs: 1, sm: 2, md: 2 },
    px: { xs: 0.5, sm: 1, md: 2 },
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
  };
  const panelHeightSx = {
    borderRadius: { xs: '14px', sm: '16px' },
    border: '1px solid rgba(15, 23, 42, 0.06)',
    boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)',
    bgcolor: '#ffffff',
    flexShrink: 0,
  };

  return (
    <Box component="main" sx={pageSx}>
      <Stack spacing={{ xs: 0.75, md: 1.25 }} sx={{ height: '100%', minHeight: 0 }}>
        <Paper elevation={0} sx={{ ...panelHeightSx, p: { xs: 1.25, sm: 1.5, md: 1.75 } }}>
          <Grid container spacing={{ xs: 1, md: 1.25 }} alignItems="center">
            <Grid item xs={12} lg={3}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6" sx={{ fontWeight: 950, fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' }, color: '#020617', lineHeight: 1.2 }}>
                    Promo Claims
                  </Typography>
                  <Typography sx={{ color: '#475569', fontSize: { xs: '0.72rem', sm: '0.78rem' }, lineHeight: 1.25, mt: 0.25 }}>
                    Issued, available, consumed, and review status.
                  </Typography>
                </Box>
                {loading ? <CircularProgress size={18} /> : null}
              </Stack>
            </Grid>

            <Grid item xs={12} lg={6}>
              <Grid container spacing={0.75}>
                <Grid item xs={6} sm={3}>
                  <CompactMetric title="Issued" value={formatClaimNumber(summary.total || 0)} subtitle="selected period" tone="neutral" />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <CompactMetric title="Available" value={formatClaimNumber(statusCounts['1'] || 0)} subtitle="claimed" tone="neutral" />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <CompactMetric title="Consumed" value={formatClaimNumber(statusCounts['2'] || 0)} subtitle="completed use" tone="positive" />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <CompactMetric title="Review" value={formatClaimNumber(needsReview)} subtitle="missed + pending" tone="warning" />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} lg={3}>
              <Stack spacing={0.75} alignItems={{ xs: 'flex-start', lg: 'flex-end' }}>
                <Chip
                  label={hasData ? (client ? 'Selected client' : 'All clients') : 'Loading claims'}
                  size="small"
                  sx={{ fontWeight: 900, bgcolor: client ? '#eff6ff' : '#f1f5f9', color: client ? '#1e3a8a' : '#334155' }}
                />
                <Box sx={{ maxWidth: '100%', overflowX: 'auto', pb: 0.25 }}>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={periodMode}
                    onChange={handlePeriodChange}
                    aria-label="Claim period"
                    disabled={loading || loadingMore || recoverySubmitting}
                    sx={{
                      bgcolor: '#fff',
                      borderRadius: '12px',
                      whiteSpace: 'nowrap',
                      '& .MuiToggleButton-root': {
                        minWidth: 40,
                        px: 0.7,
                        py: 0.35,
                        fontSize: '0.66rem',
                        fontWeight: 850,
                        textTransform: 'none',
                        '&.Mui-selected': { bgcolor: '#0f172a', color: '#fff', '&:hover': { bgcolor: '#111827' } },
                      },
                    }}
                  >
                    <ToggleButton value="today">Today</ToggleButton>
                    <ToggleButton value="last_7d">7d</ToggleButton>
                    <ToggleButton value="this_month">Month</ToggleButton>
                    <ToggleButton
                      value="custom"
                      ref={customPeriodAnchor}
                      aria-haspopup="dialog"
                      aria-expanded={customPeriodOpen}
                      aria-controls={customPeriodOpen ? 'claims-custom-period' : undefined}
                      onClick={() => { if (periodMode === 'custom') openCustomPeriod(); }}
                    >
                      Custom
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                {periodMode === 'custom' && customRange && (
                  <Typography sx={{ fontSize: '0.7rem', color: '#64748b' }}>
                    {customRange.dateFrom} – {customRange.dateTo}
                  </Typography>
                )}
                <CustomPeriodPopover
                  id="claims-custom-period"
                  open={customPeriodOpen}
                  anchorEl={customPeriodAnchor.current}
                  onClose={() => setCustomPeriodOpen(false)}
                  dateFrom={draftRange.dateFrom}
                  dateTo={draftRange.dateTo}
                  onDateFromChange={(dateFrom) => { setDraftRange((range) => ({ ...range, dateFrom })); setCustomPeriodError(''); }}
                  onDateToChange={(dateTo) => { setDraftRange((range) => ({ ...range, dateTo })); setCustomPeriodError(''); }}
                  onApply={handleCustomPeriodApply}
                  error={customPeriodError}
                />
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {error ? <Alert severity="error" sx={{ borderRadius: '12px', flexShrink: 0 }}>{error}</Alert> : null}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 2fr) minmax(320px, 1fr)' },
            gap: { xs: 0.75, md: 1.25 },
            flex: { xs: 'initial', md: 1 },
            minHeight: { xs: 'initial', md: 0 },
            width: '100%',
          }}
        >
          <Stack spacing={{ xs: 0.75, md: 1.25 }} sx={{ height: { xs: 'auto', md: '100%' }, minHeight: 0, minWidth: 0 }}>
            <Box sx={{ height: { xs: 310, md: 'auto' }, minHeight: { xs: 310, md: 0 }, flexShrink: 0, minWidth: 0 }}>
              <StatusDistribution hasData={hasData} summary={summary} />
            </Box>
            <Box sx={{ height: { xs: client ? 620 : 360, md: 'auto' }, flex: { xs: 'initial', md: 1 }, minHeight: { xs: client ? 620 : 360, md: 0 }, minWidth: 0 }}>
              {scope === 'allClients' ? (
                <DailyPromotionActivity
                  hasData={hasData}
                  rows={dailyActivity}
                  periodMode={periodMode}
                />
              ) : (
                <ClaimActivity
                  hasData={hasData}
                  claims={claims}
                  filteredTotal={filteredTotal}
                  nextCursor={nextCursor}
                  loadingMore={loadingMore}
                  filtersDirty={filtersDirty}
                  onLoadMore={() => loadPage(activeQuery, { append: true, cursor: nextCursor })}
                  canRecover={canRecover}
                  recoverySubmitting={recoverySubmitting}
                  onRecover={handleRecoveryStart}
                />
              )}
            </Box>
          </Stack>

          <Box sx={{ height: { xs: client ? 520 : 390, md: '100%' }, minHeight: 0, minWidth: 0 }}>
            <ClientLookupPanel
              lookupMode={lookupMode}
              setLookupMode={setLookupMode}
              email={email}
              setEmail={setEmail}
              claimIref={claimIref}
              setClaimIref={setClaimIref}
              selectedStatus={selectedStatus}
              setSelectedStatus={setSelectedStatus}
              periodMode={periodMode}
              scope={scope}
              hasData={hasData}
              client={client}
              loading={loading}
              loadingMore={loadingMore}
              filtersDirty={filtersDirty}
              onSearch={handleSearch}
              onClear={handleClear}
              canRecover={canRecover}
              recoveryClaim={recoveryClaim}
              recoveryResult={recoveryResult}
              recoverySubmitting={recoverySubmitting}
              matchedClaim={matchedClaim}
              onRecoveryStart={handleRecoveryStart}
              onRecoveryCancel={() => setRecoveryClaim(null)}
              onRecovered={handleRecovered}
              onRecoverySubmittingChange={setRecoverySubmitting}
            />
          </Box>
        </Box>
      </Stack>
    </Box>
  );
}

export default ClientPromotionClaims;
